import React, { useState, useEffect } from 'react';
import { ArrowRight, Calendar, Clock, User, ArrowLeft, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useTranslation } from 'react-i18next';
import { apiUrl, openBookDemo } from '../utils/config';
import logger from '../utils/logger';

const Blog = ({ onPostClick, onBackToHome, onAdminClick }) => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Reset selected category when language changes
  useEffect(() => {
    setSelectedCategory(i18n.language === 'tr' ? 'Tümü' : 'All');
  }, [i18n.language]);
  
  // Fetch posts from API
  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory, searchQuery, i18n.language]);

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'All') params.append('category', selectedCategory);
      params.append('lang', i18n.language);
      
      const response = await fetch(apiUrl(`blog/posts?${params}`));
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      logger.error('Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(apiUrl(`blog/categories?lang=${i18n.language}`));
      if (response.ok) {
        const data = await response.json();
        setCategories(data.length > 0 ? data : [i18n.language === 'tr' ? 'Tümü' : 'All']);
      }
    } catch (error) {
      logger.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPosts();
  };

  const clearSearch = () => {
    setSearchQuery('');
  };
  
  const featuredPosts = posts.filter(post => post.featured);
  const regularPosts = posts;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={onBackToHome}
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
          <button 
            onClick={onAdminClick}
            className="text-sm text-gray-500 hover:text-blue-600 transition-colors"
          >
            {i18n.language === 'tr' ? 'Yönetici Paneli' : 'Admin Panel'}
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="container mx-auto px-6 text-center">
          <p className="text-sm text-blue-400 uppercase tracking-wider mb-4 font-semibold">{t('nav.blog')}</p>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('pages.blog.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            {t('pages.blog.subtitle')}
          </p>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('pages.blog.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-12 py-6 text-lg bg-white border-0 rounded-full shadow-lg"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && !searchQuery && (
        <div className="container mx-auto px-6 -mt-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-6">
            {featuredPosts.slice(0, 2).map((post) => (
              <div 
                key={post.id}
                onClick={() => onPostClick(post.id)}
                className="bg-white rounded-2xl shadow-xl overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                      {t('pages.blog.featured')}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="text-blue-600 font-medium">{post.category}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-700">
                        {post.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="text-sm text-gray-600">{post.author}</span>
                    </div>
                    <span className="text-blue-600 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      {t('pages.blog.readMore')} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-5 py-2 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Search Results Info */}
        {searchQuery && (
          <div className="text-center mb-8">
            <p className="text-gray-600">
              {posts.length} result{posts.length !== 1 ? 's' : ''} for "<span className="font-semibold">{searchQuery}</span>"
            </p>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg mb-4">No articles found</p>
            <Button onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}>
              Clear Filters
            </Button>
          </div>
        ) : (
          /* All Posts Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post) => (
              <article 
                key={post.id}
                onClick={() => onPostClick(post.id)}
                className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {post.featured && (
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                        {t('pages.blog.featured')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="text-blue-600 font-medium">{post.category}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(post.date).toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{post.author}</span>
                    </div>
                    <span className="text-sm text-gray-500">{post.readTime}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gray-900 py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {i18n.language === 'tr' ? 'Liderlik Gelişiminizi Dönüştürmeye Hazır mısınız?' : 'Ready to Transform Your Leadership Development?'}
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            {i18n.language === 'tr' ? 'CoachMeUpp\'ın organizasyonunuzun ölçülebilir sonuçlar elde etmesine nasıl yardımcı olabileceğini görün' : 'See how CoachMeUpp can help your organization achieve measurable results'}
          </p>
          <Button 
            onClick={openBookDemo}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
          >
            {t('nav.bookDemo')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Blog;
